import { useSuspenseQuery } from '@tanstack/react-query';
import { getAdmissionHistoryOptions } from '@generated';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatToShortDate } from '~/util/date';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { createRouteToPatientChartPage } from '../charts/util';
import { useChart } from '../charts/ChartContext';

export function CertificationPeriodSelector() {
	const { patientId, stayId, certificationPeriodId } = useChart();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { data: admissions } = useSuspenseQuery(getAdmissionHistoryOptions(patientId));

	const selectedCertificationPeriod = admissions.flatMap(x => x.certificationPeriods).find(x => x.id === certificationPeriodId);

	if (!selectedCertificationPeriod) {
		throw new Error(`Certification period not found for ID: ${certificationPeriodId}`);
	}

	const sections = admissions.map(x => ({
		id: x.stayId,
		title: `Chart #${x.chartNumber} · SOC ${formatToShortDate(x.startOfCareDate)}${
			x.dischargeDate ? ` - DC ${formatToShortDate(x.dischargeDate)}` : ''
		}`,
		items: x.certificationPeriods.map(y => ({
			id: y.id,
			name: `Cert. Period #${y.certificationNumber} · ${formatToShortDate(y.startDate)} – ${formatToShortDate(y.endDate)}`
		}))
	}));

	const onChange = (value: string | number | null) => {
		const admission = admissions.find(x => x.certificationPeriods.some(y => y.id === value));
		if (!admission || typeof value !== 'string' || value === selectedCertificationPeriod.id) {
			return;
		}
		const currentBase = createRouteToPatientChartPage(patientId, stayId, certificationPeriodId);
		const remainder = pathname.startsWith(currentBase) ? pathname.slice(currentBase.length) : '';
		navigate(createRouteToPatientChartPage(patientId, admission.stayId, value) + remainder);
	};

	return (
		<SelectField
			label="Certification period"
			hideLabel
			density="compact"
			value={selectedCertificationPeriod.id}
			sections={sections}
			onChange={onChange}
			containerClassName="w-72"
			className="border-gray-200 bg-transparent shadow-none hover:bg-gray-50"
		/>
	);
}
